/**
 * New-app form at `/apps/new` — reached from the "Create app" item in
 * the Apps page's split-button dropdown. Captures the bare minimum to
 * provision a Kin app (name + slug + optional description / database
 * flag), POSTs to `/api/apps`, then sends the user straight into their
 * new app's preview with `?kin=open` so the preview widget auto-opens
 * and greets them with "What do you want to build?".
 *
 * The slug is auto-derived from the name as the user types and stays
 * editable. Region is hardcoded to ENAM (Mytra HQ) — it only affects
 * D1's primary-write-location hint when a database is provisioned,
 * and for internal read-heavy apps the choice is decision-noise. The
 * MCP `kin_create_app` tool still exposes the full surface for
 * scripted provisioning. `needs_database` defaults to TRUE — most
 * non-developer flows are tracker / form / polls apps that benefit
 * from a backend; a single checkbox lets the user opt out for pure
 * static / info pages.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, apiMessage, createExternalApp } from '../api/client.js';
import type { MeResponse } from '../api/types.js';
import { Banner, Button, ButtonLink, Card, PageHeader, inputClass } from '../components/ui.js';

/**
 * Hardcoded region — Mytra HQ default. See header comment for
 * rationale. Power users who care can still set this via the MCP
 * `kin_create_app` tool or hit `/api/apps` directly.
 */
const DEFAULT_REGION = 'ENAM';

const SLUG_PATTERN = /^[a-z][a-z0-9-]{1,62}[a-z0-9]$/;

interface CreateAppResponse {
  slug: string;
  hostnames: { preview: string; live: string };
}

/**
 * Provisioning model picked at create time.
 *
 *   - `managed` — the existing default: the platform owns the Worker /
 *     D1 / R2 and the build-via-chat flow. Available to everyone.
 *   - `external` — the app is registered against caller-supplied
 *     hostnames and authenticates purely via bearer tokens. No platform
 *     compute is provisioned, so the managed-only fields (description,
 *     database) don't apply. Platform-admin only; the option isn't even
 *     rendered for non-admins (the control plane enforces it too).
 */
type AppKind = 'managed' | 'external';

/**
 * Parse a free-text hostnames field (comma- or newline-separated, or
 * both) into a deduped, trimmed list. Matches the Portal idiom of
 * accepting either separator without forcing a repeated-field UI.
 */
function parseHostnames(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[\n,]+/)) {
    const host = part.trim();
    if (host && !seen.has(host)) {
      seen.add(host);
      out.push(host);
    }
  }
  return out;
}

export function AppNew({ me }: { me: MeResponse }) {
  const navigate = useNavigate();
  const isAdmin = me.globalRole === 'platform_admin';

  const [kind, setKind] = useState<AppKind>('managed');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [description, setDescription] = useState('');
  const [needsDatabase, setNeedsDatabase] = useState(true);
  const [hostnames, setHostnames] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-derive slug from name until the user touches the slug field.
  useEffect(() => {
    if (slugManuallyEdited) return;
    setSlug(slugify(name));
  }, [name, slugManuallyEdited]);

  // Defense in depth: a non-admin can never end up on the External branch
  // even if state were nudged — but the option isn't rendered for them.
  const isExternal = kind === 'external' && isAdmin;
  const parsedHostnames = useMemo(() => parseHostnames(hostnames), [hostnames]);

  const slugValid = useMemo(() => slug.length === 0 || SLUG_PATTERN.test(slug), [slug]);
  const canSubmit =
    name.trim().length > 0 &&
    slug.length > 0 &&
    slugValid &&
    !submitting &&
    (!isExternal || parsedHostnames.length > 0);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isExternal) {
        const result = await createExternalApp({
          slug,
          name: name.trim(),
          region: DEFAULT_REGION,
          external_hostnames: parsedHostnames,
        });
        // External apps have no preview to bounce into — go straight to
        // the in-Portal app page, where the bearer-token / paths panel
        // lives. Same-origin, so react-router navigation is right here.
        navigate(`/apps/${result.slug}`);
        return;
      }
      const result = await api.post<CreateAppResponse>('/api/apps', {
        slug,
        name: name.trim(),
        region: DEFAULT_REGION,
        ...(description.trim() ? { description: description.trim() } : {}),
        needs_database: needsDatabase,
      });
      // Redirect to the preview URL with `?kin=open` so the widget
      // auto-opens its panel. Cross-origin redirect (preview is on a
      // different subdomain), so we use a full-page navigation rather
      // than react-router's `navigate`.
      window.location.href = `https://${result.hostnames.preview}/?kin=open`;
    } catch (err) {
      setError(apiMessage(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-in max-w-2xl">
      <PageHeader
        back={{ to: '/', label: 'Apps' }}
        title="Create app"
        description="Name your app and we'll provision it. Then you'll chat with Kin to build it."
      />

      {error && <Banner tone="error">{error}</Banner>}

      <Card>
        <form onSubmit={onSubmit} className="p-5 space-y-5">
          {isAdmin && (
            <fieldset>
              <legend className="block text-sm font-medium text-ink mb-1.5">App type</legend>
              <div className="inline-flex rounded-lg border border-line bg-surface p-0.5">
                {(['managed', 'external'] as const).map((value) => {
                  const active = kind === value;
                  return (
                    <label
                      key={value}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors cursor-pointer ${
                        active ? 'bg-brand text-brand-fg' : 'text-muted hover:text-ink'
                      }`}
                    >
                      <input
                        type="radio"
                        name="app-kind"
                        value={value}
                        checked={active}
                        onChange={() => setKind(value)}
                        className="sr-only"
                      />
                      {value === 'managed' ? 'Managed' : 'External'}
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-muted mt-1.5">
                {isExternal
                  ? 'Register an app you host elsewhere. Authenticated by bearer token against the hostnames below — no platform compute is provisioned.'
                  : 'We provision and host the app for you, then you build it by chatting with Kin.'}
              </p>
            </fieldset>
          )}

          <Field
            label="Name"
            hint="A short, human-readable label — e.g. “Lunch Tracker”."
            htmlFor="app-name"
          >
            <input
              id="app-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              maxLength={80}
              className={inputClass}
              placeholder="My app"
            />
          </Field>

          <Field
            label="Slug"
            hint={
              isExternal
                ? 'URL-safe identifier for this app in Kin. Auto-derived from the name; tweak if you like.'
                : slug
                  ? `Your preview will live at preview-${slug}.${typeof window !== 'undefined' ? window.location.hostname : 'mkin.app'}`
                  : 'URL-safe identifier. Auto-derived from the name; tweak if you like.'
            }
            htmlFor="app-slug"
            error={
              !slugValid && slug.length > 0
                ? 'Slug must be 3–64 lowercase chars: letters, digits, hyphens. Must start with a letter and end with a letter or digit.'
                : null
            }
          >
            <div className="relative">
              <input
                id="app-slug"
                type="text"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value.toLowerCase());
                  setSlugManuallyEdited(true);
                }}
                required
                pattern="^[a-z][a-z0-9-]{1,62}[a-z0-9]$"
                maxLength={64}
                className={`${inputClass} font-mono`}
                placeholder="my-app"
              />
            </div>
          </Field>

          {isExternal ? (
            <Field
              label="Hostnames"
              hint="One or more hostnames this app answers on. Separate with commas or new lines — e.g. service.example.com."
              htmlFor="app-hostnames"
            >
              <textarea
                id="app-hostnames"
                value={hostnames}
                onChange={(e) => setHostnames(e.target.value)}
                required
                rows={3}
                className={`${inputClass} font-mono`}
                placeholder="service.example.com"
              />
            </Field>
          ) : (
            <>
              <Field
                label="Description"
                hint="One sentence so teammates know what this app does."
                htmlFor="app-description"
                optional
              >
                <input
                  id="app-description"
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={200}
                  className={inputClass}
                  placeholder="Track our team's lunch orders"
                />
              </Field>

              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={needsDatabase}
                  onChange={(e) => setNeedsDatabase(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-line text-brand focus:ring-brand/50"
                />
                <span>
                  <span className="text-sm text-ink font-medium block">Needs a database</span>
                  <span className="text-xs text-muted block mt-0.5">
                    Check this if your app needs to save data — orders, votes, signups, anything
                    that persists. Uncheck for a static page or calculator.
                  </span>
                </span>
              </label>
            </>
          )}

          <div className="pt-2 flex items-center justify-end gap-2 border-t border-line">
            <ButtonLink to="/" variant="ghost" size="sm">
              Cancel
            </ButtonLink>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              loading={submitting}
              disabled={!canSubmit}
            >
              Create app
            </Button>
          </div>
        </form>
      </Card>

      <p className="text-xs text-muted mt-4">
        Want the full reference?{' '}
        <Link to="/docs/getting-started" className="text-brand hover:underline">
          Read the getting-started guide
        </Link>
        .
      </p>
    </div>
  );
}

function Field({
  label,
  hint,
  htmlFor,
  optional,
  error,
  children,
}: {
  label: string;
  hint?: string;
  htmlFor: string;
  optional?: boolean;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-ink mb-1">
        {label}
        {optional && <span className="ml-1 text-xs text-faint font-normal">(optional)</span>}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-rose-600 dark:text-rose-400 mt-1.5">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted mt-1.5">{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * Best-effort slugify for the auto-derive case. Same shape the server
 * validates against (`^[a-z][a-z0-9-]{1,62}[a-z0-9]$`) — we don't try
 * to enforce the full pattern client-side, just normalize the obvious
 * cases (lowercase, ASCII-ize punctuation to hyphens, collapse runs).
 * The server is the source of truth.
 */
function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip accents
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export default AppNew;
